import { useEffect, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import type { Me } from '../auth/authApi.ts';
import { loadMe, quotaLine, signOut } from '../lib/auth.ts';
import { ADMIN_PAGES, routePath } from '../lib/router.ts';
import type { AdminPage } from '../lib/router.ts';

const MENU_ID = 'account-menu';
const ITEM = '[data-menu-item]';

const ADMIN_LABEL: Record<AdminPage, string> = { users: 'Users', runs: 'Runs', health: 'Health' };

/** The eval tool is its own bundle, mounted at a real path, not a hash route (spec section 9). */
const EVALS_HREF = '/evals';

/**
 * The header account menu (docs/design-system.md, "Menu"). It is a disclosure, not an ARIA `menu`:
 * its rows are links and one button, so Tab already reaches each of them, and a disclosure asks
 * for no key handling that a screen reader user has to guess at. On top of that floor: Escape
 * closes and returns focus to the trigger, the arrow keys move between rows, and a click or a
 * focus move outside closes it.
 */
export function AccountMenu({ me }: { me: Me }): JSX.Element {
  const [open, setOpen] = useState(false);
  const [signOutError, setSignOutError] = useState<string | null>(null);
  const anchorRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  /** The key that opened the menu, if a key did: its row takes focus once the rows exist. */
  const openedWith = useRef<string | null>(null);

  const items = (): HTMLElement[] =>
    Array.from(anchorRef.current?.querySelectorAll<HTMLElement>(ITEM) ?? []);

  useEffect(() => {
    if (!open) return;
    // The runs-left count moves when a run launches, so ask again each time the menu opens.
    void loadMe();
    if (openedWith.current) {
      const rows = items();
      rows[nextRowIndex(openedWith.current, -1, rows.length)]?.focus();
      openedWith.current = null;
    }
    // Safari does not focus a clicked button, so a blur is not enough to catch an outside click.
    function onPointerDown(event: MouseEvent): void {
      if (!anchorRef.current?.contains(event.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>): void {
    if (event.key === 'Escape' && open) {
      event.preventDefault();
      setOpen(false);
      triggerRef.current?.focus();
      return;
    }
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    if (!open) {
      openedWith.current = event.key;
      setOpen(true);
      return;
    }
    const rows = items();
    if (rows.length === 0) return;
    const at = rows.indexOf(document.activeElement as HTMLElement);
    rows[nextRowIndex(event.key, at, rows.length)]?.focus();
  }

  async function onSignOut(): Promise<void> {
    setSignOutError(null);
    try {
      await signOut();
    } catch (e) {
      setSignOutError(`Sign-out failed: ${e instanceof Error ? e.message : String(e)}`);
    }
  }

  return (
    <div
      className="menu-anchor"
      ref={anchorRef}
      onKeyDown={onKeyDown}
      onBlur={(event) => {
        if (!anchorRef.current?.contains(event.relatedTarget)) setOpen(false);
      }}
    >
      <button
        ref={triggerRef}
        type="button"
        className="btn ghost-light sm account-trigger"
        aria-expanded={open}
        aria-controls={MENU_ID}
        aria-label={`Account menu for ${me.email}`}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="account-trigger-text">{me.email}</span>
      </button>
      {open && (
        <AccountMenuPanel
          me={me}
          signOutError={signOutError}
          onNavigate={() => setOpen(false)}
          onSignOut={() => void onSignOut()}
        />
      )}
    </div>
  );
}

/**
 * The row an arrow key moves focus to. `at` is -1 when focus is not on a row — it is on the
 * trigger — and that case has its own answer: down enters at the first row, up enters at the last.
 * Plain modular arithmetic on -1 would send ArrowUp to the second-to-last row.
 */
export function nextRowIndex(key: string, at: number, count: number): number {
  if (key === 'Home') return 0;
  if (key === 'End') return count - 1;
  if (at < 0) return key === 'ArrowUp' ? count - 1 : 0;
  return key === 'ArrowUp' ? (at - 1 + count) % count : (at + 1) % count;
}

/**
 * The open menu, as a function of the account. Apart from the disclosure above so that a test can
 * render it: which rows an account gets is the part of the menu that carries a rule.
 */
export function AccountMenuPanel({
  me,
  signOutError,
  onNavigate,
  onSignOut,
}: {
  me: Me;
  signOutError?: string | null;
  onNavigate?: () => void;
  onSignOut?: () => void;
}): JSX.Element {
  const quota = quotaLine(me);
  return (
    <ul id={MENU_ID} className="menu" aria-label="Account">
      <li>
        <span className="menu-readout">{me.email}</span>
        {/* No quota, no line. It is never rendered as "unlimited" (spec section 5). */}
        {quota && <span className="menu-readout">{quota}</span>}
      </li>
      <li className="menu-sep">
        <a className="menu-item" data-menu-item href={routePath({ view: 'account' })} onClick={onNavigate}>
          Account
        </a>
      </li>
      {me.role === 'admin' && (
        <li className="menu-sep">
          <span className="label">Admin</span>
          {ADMIN_PAGES.map((page) => (
            <a
              key={page}
              className="menu-item"
              data-menu-item
              href={routePath({ view: 'admin', page })}
              onClick={onNavigate}
            >
              {ADMIN_LABEL[page]}
            </a>
          ))}
          <a className="menu-item" data-menu-item href={EVALS_HREF}>
            Evals
          </a>
        </li>
      )}
      <li className="menu-sep">
        {signOutError && (
          <p className="notice err" role="alert">
            {signOutError}
          </p>
        )}
        <button className="menu-item" data-menu-item type="button" onClick={onSignOut}>
          Sign out
        </button>
      </li>
    </ul>
  );
}
